import { useMemo } from 'react';

// Type definitions for the View Transition API
interface ViewTransition {
  readonly finished: Promise<void>;
  readonly ready: Promise<void>;
  readonly updateCallbackDone: Promise<void>;
  skipTransition(): void;
}

type DocumentWithViewTransition = Document & {
  startViewTransition?: (cb: () => void) => ViewTransition;
};

function hasViewTransition(doc: DocumentWithViewTransition): doc is Document & {
  startViewTransition: (cb: () => void) => ViewTransition;
} {
  return typeof doc.startViewTransition === 'function';
}

// This hook provides a function to safely use the startViewTransition method if available.
export function useViewTransition() {
  // useMemo is used to memoize the function, ensuring it doesn't change between renders unless dependencies change.
  return useMemo<((callback: () => void) => ViewTransition) | null>(() => {
    // Check if the document object is available (i.e., we're in a supported browser environment).
    if (typeof document !== 'undefined') {
      const doc = document as DocumentWithViewTransition;
      if (hasViewTransition(doc)) {
        // Return a function that calls startViewTransition with the provided callback
        return (callback: () => void): ViewTransition =>
          doc.startViewTransition(callback);
      }
    }
    // If startViewTransition is not available, return null.
    return null;
  }, []); // The empty dependency array ensures this logic runs only once when the component mounts.
}
